/**
 * Stuller Band Builder Integration
 *
 * Stuller offers two integration methods for their Ring Builder tool:
 *   1. Embedded iframe widget (requires Stuller Account ID)
 *   2. REST API (requires OAuth credentials — client_id/client_secret)
 *
 * Once Stuller API credentials are available, set STULLER_ACCOUNT_ID below
 * to enable the embedded builder. Until then, a functional fallback UI
 * is shown that lets customers design a band and submit as an inquiry.
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════
     CONFIG — Stuller Showcase / Wedding Band Builder embed
     LIVE — uses LA Diamond's official Stuller embed URL.
     ══════════════════════════════════════════ */
  var STULLER_ACCOUNT_ID = '';          // Reserved (not needed when full embed URL is set)
  var STULLER_EMBED_URL = 'https://ladiamond1-frame-categoryembed.jewelershowcase.com/band-builder';
  var STULLER_API_CLIENT_ID = '';       // Reserved for deeper REST API integration
  var STULLER_API_CLIENT_SECRET = '';   // Reserved for deeper REST API integration

  /* Fallback URL template (only used if STULLER_ACCOUNT_ID is set without a full URL) */
  var STULLER_DEFAULT_EMBED = 'https://www.stuller.com/sharetopia/builder/?category=weddingbands&acct=';

  /* ══════════════════════════════════════════
     Init
     ══════════════════════════════════════════ */
  function init() {
    var container = document.getElementById('stullerBandBuilder');
    if (!container) return;

    if (STULLER_ACCOUNT_ID || STULLER_EMBED_URL) {
      loadStullerEmbed(container);
    } else {
      renderFallbackBuilder(container);
    }
  }

  /* ══════════════════════════════════════════
     Mode 1: Stuller iframe embed (live)
     Uses Stuller's recommended attributes for the Showcase iframe.
     ══════════════════════════════════════════ */
  function loadStullerEmbed(container) {
    var url = STULLER_EMBED_URL || (STULLER_DEFAULT_EMBED + encodeURIComponent(STULLER_ACCOUNT_ID));
    container.classList.add('stuller-builder-live');
    container.innerHTML =
      '<iframe src="' + url + '" ' +
      'title="Stuller Wedding Band Builder" ' +
      'style="width:1px;min-width:100%;border:none;display:block;" ' +
      'width="100%" height="1500" ' +
      'scrolling="yes" frameborder="0" ' +
      'sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-top-navigation-by-user-activation" ' +
      'allow="fullscreen" allowfullscreen></iframe>';
  }

  /* ══════════════════════════════════════════
     Mode 2: Fallback custom builder UI
     (Collects specs, submits as an inquiry)
     ══════════════════════════════════════════ */
  function renderFallbackBuilder(container) {
    container.innerHTML = buildFallbackHTML();
    attachFallbackHandlers(container);
  }

  function buildFallbackHTML() {
    return '' +
      '<div class="sbb-wrapper">' +
        '<div class="sbb-header">' +
          '<h2>Design Your Custom Wedding Band</h2>' +
          '<p>Select your options below. Our team will confirm pricing and craftsmanship details within 24 hours.</p>' +
        '</div>' +

        '<div class="sbb-layout">' +

          /* LEFT: Live preview */
          '<div class="sbb-preview">' +
            '<div class="sbb-preview-box" id="sbbPreview">' +
              '<svg viewBox="0 0 300 300" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">' +
                '<defs>' +
                  '<radialGradient id="sbb-bg-grad" cx="50%" cy="50%" r="60%">' +
                    '<stop offset="0%" stop-color="#fff"/>' +
                    '<stop offset="100%" stop-color="#f4ece4"/>' +
                  '</radialGradient>' +
                  '<linearGradient id="sbb-band-grad" x1="0%" y1="0%" x2="0%" y2="100%">' +
                    '<stop offset="0%" stop-color="#e5c572" id="sbb-band-top"/>' +
                    '<stop offset="50%" stop-color="#d4b360" id="sbb-band-mid"/>' +
                    '<stop offset="100%" stop-color="#b8954c" id="sbb-band-bot"/>' +
                  '</linearGradient>' +
                '</defs>' +
                '<rect width="300" height="300" fill="url(#sbb-bg-grad)"/>' +
                '<ellipse cx="150" cy="170" rx="90" ry="35" fill="none" stroke="#232323" stroke-width="1" stroke-opacity="0.3"/>' +
                '<ellipse cx="150" cy="130" rx="90" ry="35" fill="none" stroke="#232323" stroke-width="1" stroke-opacity="0.3"/>' +
                '<path id="sbbBandShape" d="M 60 130 L 240 130 L 235 170 L 65 170 Z" fill="url(#sbb-band-grad)" stroke="#8a6f30" stroke-width="1.5"/>' +
                '<g id="sbbDiamonds"></g>' +
              '</svg>' +
              '<div class="sbb-preview-label" id="sbbPreviewLabel">14k Yellow Gold · 4mm · Classic</div>' +
            '</div>' +
            '<div class="sbb-price">' +
              '<span class="sbb-price-label">Estimated Starting Price</span>' +
              '<span class="sbb-price-value" id="sbbPriceValue">$1,200</span>' +
              '<span class="sbb-price-note">Final pricing confirmed by our team</span>' +
            '</div>' +
          '</div>' +

          /* RIGHT: Options */
          '<div class="sbb-options">' +

            /* Metal */
            '<div class="sbb-opt-group">' +
              '<label class="sbb-opt-label">Metal</label>' +
              '<div class="sbb-metal-grid">' +
                metalBtn('14k Yellow Gold', '#e5c572', '14ky', true) +
                metalBtn('14k White Gold', '#e8e8e8', '14kw') +
                metalBtn('14k Rose Gold', '#e8b4b8', '14kr') +
                metalBtn('18k Yellow Gold', '#ffcc33', '18ky') +
                metalBtn('18k White Gold', '#f0f0f0', '18kw') +
                metalBtn('Platinum', '#d4d4d4', 'plat') +
              '</div>' +
            '</div>' +

            /* Width */
            '<div class="sbb-opt-group">' +
              '<label class="sbb-opt-label">Width: <strong id="sbbWidthValue">4mm</strong></label>' +
              '<input type="range" min="2" max="10" value="4" step="0.5" id="sbbWidth" class="sbb-range">' +
              '<div class="sbb-range-ticks"><span>2mm</span><span>6mm</span><span>10mm</span></div>' +
            '</div>' +

            /* Style */
            '<div class="sbb-opt-group">' +
              '<label class="sbb-opt-label">Style</label>' +
              '<div class="sbb-style-grid">' +
                styleBtn('Classic', 'classic', true) +
                styleBtn('Diamond', 'diamond') +
                styleBtn('Milgrain', 'milgrain') +
                styleBtn('Hammered', 'hammered') +
                styleBtn('Two-Tone', 'twotone') +
                styleBtn('Beveled', 'beveled') +
              '</div>' +
            '</div>' +

            /* Finish */
            '<div class="sbb-opt-group">' +
              '<label class="sbb-opt-label">Finish</label>' +
              '<div class="sbb-finish-grid">' +
                '<button type="button" class="sbb-finish-btn active" data-finish="polished">Polished</button>' +
                '<button type="button" class="sbb-finish-btn" data-finish="satin">Satin</button>' +
                '<button type="button" class="sbb-finish-btn" data-finish="matte">Matte</button>' +
                '<button type="button" class="sbb-finish-btn" data-finish="brushed">Brushed</button>' +
              '</div>' +
            '</div>' +

            /* Ring Size */
            '<div class="sbb-opt-group">' +
              '<label class="sbb-opt-label">Ring Size (US) <a href="/pages/ring-sizing" class="sbb-size-link">Size Guide</a></label>' +
              '<select id="sbbSize" class="sbb-select">' +
                sizeOptions() +
              '</select>' +
            '</div>' +

            /* Engraving */
            '<div class="sbb-opt-group">' +
              '<label class="sbb-opt-label">Engraving (optional, up to 25 characters)</label>' +
              '<input type="text" id="sbbEngraving" maxlength="25" class="sbb-input" placeholder="e.g., Forever yours, M &amp; J">' +
            '</div>' +

            /* CTA */
            '<button type="button" id="sbbSubmit" class="sbb-submit-btn">' +
              'Request Custom Quote' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-left:8px"><polyline points="9 18 15 12 9 6"/></svg>' +
            '</button>' +
            '<p class="sbb-cta-note">Our team will respond within 24 hours with detailed pricing and craftsmanship timeline.</p>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function metalBtn(label, color, slug, active) {
    return '<button type="button" class="sbb-metal-btn' + (active ? ' active' : '') +
      '" data-metal="' + slug + '" data-label="' + label + '" data-color="' + color + '">' +
      '<span class="sbb-metal-sw" style="background:' + color + ';' +
      (color === '#f0f0f0' || color === '#e8e8e8' || color === '#d4d4d4' ? 'border:1px solid #ccc;' : '') + '"></span>' +
      '<span class="sbb-metal-lbl">' + label + '</span></button>';
  }

  function styleBtn(label, slug, active) {
    return '<button type="button" class="sbb-style-btn' + (active ? ' active' : '') +
      '" data-style="' + slug + '">' + label + '</button>';
  }

  function sizeOptions() {
    var html = '<option value="">Select Size</option>';
    for (var i = 4; i <= 14; i += 0.5) {
      html += '<option value="' + i + '"' + (i === 7 ? ' selected' : '') + '>' + i + '</option>';
    }
    return html;
  }

  /* ══════════════════════════════════════════
     Fallback builder state + handlers
     ══════════════════════════════════════════ */
  var state = {
    metalLabel: '14k Yellow Gold',
    metalSlug: '14ky',
    metalColor: '#e5c572',
    width: 4,
    style: 'classic',
    finish: 'polished',
    size: 7,
    engraving: ''
  };

  /* Base prices (starting at) by metal */
  var BASE_PRICES = {
    '14ky': 1200, '14kw': 1200, '14kr': 1250,
    '18ky': 1800, '18kw': 1800,
    'plat': 2400
  };
  var STYLE_MULTIPLIER = {
    classic: 1.0, diamond: 1.9, milgrain: 1.2,
    hammered: 1.15, twotone: 1.3, beveled: 1.1
  };

  function attachFallbackHandlers(container) {
    // Metal buttons
    container.querySelectorAll('.sbb-metal-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        container.querySelectorAll('.sbb-metal-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        state.metalLabel = btn.dataset.label;
        state.metalSlug = btn.dataset.metal;
        state.metalColor = btn.dataset.color;
        updatePreview(container);
      });
    });

    // Width range
    var widthInput = container.querySelector('#sbbWidth');
    var widthValue = container.querySelector('#sbbWidthValue');
    widthInput.addEventListener('input', function () {
      state.width = parseFloat(this.value);
      widthValue.textContent = state.width + 'mm';
      updatePreview(container);
    });

    // Style buttons
    container.querySelectorAll('.sbb-style-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        container.querySelectorAll('.sbb-style-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        state.style = btn.dataset.style;
        updatePreview(container);
      });
    });

    // Finish buttons
    container.querySelectorAll('.sbb-finish-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        container.querySelectorAll('.sbb-finish-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        state.finish = btn.dataset.finish;
        updatePreview(container);
      });
    });

    // Size
    container.querySelector('#sbbSize').addEventListener('change', function () {
      state.size = parseFloat(this.value) || 7;
    });

    // Engraving
    container.querySelector('#sbbEngraving').addEventListener('input', function () {
      state.engraving = this.value;
    });

    // Submit
    container.querySelector('#sbbSubmit').addEventListener('click', function () {
      submitCustomQuote();
    });

    updatePreview(container);
  }

  function updatePreview(container) {
    // Update band color
    var topStop = container.querySelector('#sbb-band-top');
    var midStop = container.querySelector('#sbb-band-mid');
    var botStop = container.querySelector('#sbb-band-bot');
    var shades = getShades(state.metalColor);
    topStop.setAttribute('stop-color', shades[0]);
    midStop.setAttribute('stop-color', shades[1]);
    botStop.setAttribute('stop-color', shades[2]);

    // Update band shape based on width
    var bandShape = container.querySelector('#sbbBandShape');
    var w = state.width;
    var yTop = 150 - (w * 2);
    var yBot = 150 + (w * 2);
    bandShape.setAttribute('d',
      'M 60 ' + yTop + ' L 240 ' + yTop + ' L 235 ' + yBot + ' L 65 ' + yBot + ' Z'
    );

    // Update diamonds based on style
    var diamondsG = container.querySelector('#sbbDiamonds');
    diamondsG.innerHTML = '';
    if (state.style === 'diamond') {
      for (var i = 0; i < 11; i++) {
        var x = 80 + (i * 14);
        var y = 150;
        diamondsG.innerHTML +=
          '<circle cx="' + x + '" cy="' + y + '" r="3" fill="#fff" stroke="#cccccc" stroke-width="0.5"/>' +
          '<circle cx="' + x + '" cy="' + y + '" r="1.5" fill="#fff" opacity="0.7"/>';
      }
    } else if (state.style === 'milgrain') {
      for (var j = 0; j < 30; j++) {
        var mx = 65 + (j * 6);
        diamondsG.innerHTML +=
          '<circle cx="' + mx + '" cy="' + (yTop + 2) + '" r="1" fill="#8a6f30"/>' +
          '<circle cx="' + mx + '" cy="' + (yBot - 2) + '" r="1" fill="#8a6f30"/>';
      }
    } else if (state.style === 'hammered') {
      for (var k = 0; k < 24; k++) {
        var hx = 70 + (k * 7);
        var hy = yTop + 4 + ((k % 3) * 8);
        diamondsG.innerHTML +=
          '<ellipse cx="' + hx + '" cy="' + hy + '" rx="3" ry="1.5" fill="none" stroke="#8a6f30" stroke-width="0.5" opacity="0.4"/>';
      }
    } else if (state.style === 'twotone') {
      diamondsG.innerHTML =
        '<path d="M 60 ' + (yTop + (yBot - yTop) / 3) + ' L 240 ' + (yTop + (yBot - yTop) / 3) + '" stroke="#a0a0a0" stroke-width="2"/>' +
        '<path d="M 60 ' + (yTop + 2 * (yBot - yTop) / 3) + ' L 240 ' + (yTop + 2 * (yBot - yTop) / 3) + '" stroke="#a0a0a0" stroke-width="2"/>';
    } else if (state.style === 'beveled') {
      diamondsG.innerHTML =
        '<path d="M 60 ' + (yTop + 3) + ' L 240 ' + (yTop + 3) + '" stroke="#8a6f30" stroke-width="0.8"/>' +
        '<path d="M 60 ' + (yBot - 3) + ' L 240 ' + (yBot - 3) + '" stroke="#8a6f30" stroke-width="0.8"/>';
    }

    // Update label
    var styleLabel = state.style.charAt(0).toUpperCase() + state.style.slice(1);
    container.querySelector('#sbbPreviewLabel').textContent =
      state.metalLabel + ' · ' + state.width + 'mm · ' + styleLabel;

    // Update price
    var basePrice = BASE_PRICES[state.metalSlug] || 1200;
    var styleMult = STYLE_MULTIPLIER[state.style] || 1;
    var widthMult = 0.8 + (state.width / 10);
    var finalPrice = Math.round(basePrice * styleMult * widthMult);
    container.querySelector('#sbbPriceValue').textContent = '$' + finalPrice.toLocaleString();
  }

  function getShades(hex) {
    /* Return 3 shades: lighter, base, darker for gradient */
    var c = hex.replace('#', '');
    var r = parseInt(c.substring(0, 2), 16);
    var g = parseInt(c.substring(2, 4), 16);
    var b = parseInt(c.substring(4, 6), 16);
    function shade(pct) {
      var f = pct < 0 ? 0 : 255;
      var p = Math.abs(pct);
      var nr = Math.round((f - r) * p + r);
      var ng = Math.round((f - g) * p + g);
      var nb = Math.round((f - b) * p + b);
      return 'rgb(' + nr + ',' + ng + ',' + nb + ')';
    }
    return [shade(0.2), hex, shade(-0.25)];
  }

  function submitCustomQuote() {
    var styleLabel = state.style.charAt(0).toUpperCase() + state.style.slice(1);
    var finishLabel = state.finish.charAt(0).toUpperCase() + state.finish.slice(1);
    var priceEl = document.getElementById('sbbPriceValue');
    var priceTxt = priceEl ? priceEl.textContent : '';

    var subject = encodeURIComponent('Custom Wedding Band Quote Request');
    var body = encodeURIComponent(
      'I would like to request a custom wedding band with the following specs:\n\n' +
      'Metal: ' + state.metalLabel + '\n' +
      'Width: ' + state.width + 'mm\n' +
      'Style: ' + styleLabel + '\n' +
      'Finish: ' + finishLabel + '\n' +
      'Ring Size (US): ' + state.size + '\n' +
      (state.engraving ? 'Engraving: "' + state.engraving + '"\n' : '') +
      'Estimated Starting Price: ' + priceTxt + '\n\n' +
      'Please confirm final pricing and expected craftsmanship timeline.'
    );

    window.location.href = '/pages/contact?subject=' + subject + '&body=' + body;
  }

  /* Start */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
